import React, { Fragment } from "react";
import { useLocation } from "react-router-dom";

interface FolderListProps {
  foldersTotal: number;
  activeFolder: number;
  invalidFolders?: number[];
  visibleFolders?: number[];
  /**
   * Шапка редактора эфира — одна строка: номера папок и поле «#»/Go не переносятся. Раньше, когда справа
   * появлялся счётчик «правок в работе», «#»/Go уходили на вторую строку и лента съезжала на 20 px (P50m10, E6).
   */
  oneLine?: boolean;
}

export function FolderList({
  foldersTotal,
  activeFolder,
  invalidFolders = [],
  visibleFolders,
  oneLine = false,
}: FolderListProps) {
  const folders: React.ReactNode[] = [];
  const location = useLocation();
  const [targetFolder, setTargetFolder] = React.useState<string>("");

  const allowedFolders = React.useMemo(() => {
    if (Array.isArray(visibleFolders)) {
      return Array.from(
        new Set(
          visibleFolders.filter(
            (folder) =>
              Number.isInteger(folder) && folder >= 1 && folder <= foldersTotal,
          ),
        ),
      ).sort((a, b) => a - b);
    }
    return Array.from({ length: foldersTotal }, (_, index) => index + 1);
  }, [visibleFolders, foldersTotal]);

  const isFiltered = Array.isArray(visibleFolders);
  const allowedFoldersSet = React.useMemo(
    () => new Set<number>(allowedFolders),
    [allowedFolders],
  );

  const isFolderAllowed = React.useCallback(
    (folderNum: number) =>
      folderNum >= 1 &&
      folderNum <= foldersTotal &&
      (!isFiltered || allowedFoldersSet.has(folderNum)),
    [allowedFoldersSet, foldersTotal, isFiltered],
  );

  const updateFolderParam = (i: number) => {
    if (!isFolderAllowed(i)) {
      return;
    }

    // Create a new URLSearchParams object from the current location
    const searchParams = new URLSearchParams(location.search);

    // Update the folder parameter
    searchParams.set('folder', `${i}`);

    // Hard reload to fully reset audio state
    window.location.href = `${location.pathname}?${searchParams.toString()}`;
  };

  const handleJump = () => {
    const folderNum = parseInt(targetFolder, 10);
    if (!isNaN(folderNum) && isFolderAllowed(folderNum)) {
      updateFolderParam(folderNum);
      setTargetFolder("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleJump();
    }
  };

  const firstAllowedFolder = allowedFolders[0];
  const secondAllowedFolder = allowedFolders[1];
  const lastAllowedFolder = allowedFolders[allowedFolders.length - 1];
  const secondLastAllowedFolder = allowedFolders[allowedFolders.length - 2];

  const renderF = (i: number) => {
    const className = i === activeFolder ? 'x_head_chapter active' : 'x_head_chapter';

    if (
      i === activeFolder ||
      (!(i > activeFolder + 2) && !(i < activeFolder - 2)) ||
      i === lastAllowedFolder ||
      i === secondLastAllowedFolder ||
      i === firstAllowedFolder ||
      i === secondAllowedFolder
    ) {
      return (
        <span
          onClick={() => updateFolderParam(i)}
          key={`folder_${i}`}
          className={className}
          style={{
            cursor: 'pointer',
            ...(invalidFolders.includes(i) ? { color: 'red', borderColor: 'red' } : {})
          }}
        >
          {i}
        </span>
      );
    } else {
      return <Fragment key={`folder_dot_${i}`}>.</Fragment>;
    }
  };

  for (const i of allowedFolders) {
    folders.push(renderF(i));
  }

  const targetFolderNum = parseInt(targetFolder, 10);
  const isGoEnabled = !isNaN(targetFolderNum) && isFolderAllowed(targetFolderNum);

  return (
    <div
      data-testid="folder-list"
      style={{ display: 'flex', alignItems: 'center', flexWrap: oneLine ? 'nowrap' : 'wrap', gap: '4px',
        ...(oneLine ? { whiteSpace: 'nowrap' as const, flex: 'none' } : {}) }}
    >
      {folders}
      <div style={{ marginLeft: '10px', display: 'flex', alignItems: 'center', gap: '4px', ...(oneLine ? { flex: 'none' } : {}) }}>
        <input
          type="number"
          value={targetFolder}
          onChange={(e) => setTargetFolder(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            width: '50px',
            padding: '2px 4px',
            fontSize: '12px',
            borderRadius: '4px',
            border: '1px solid #ccc'
          }}
          placeholder="#"
        />
        <button
          onClick={handleJump}
          disabled={!isGoEnabled}
          style={{
            padding: '2px 6px',
            fontSize: '12px',
            cursor: 'pointer',
            borderRadius: '4px',
            border: '1px solid #ccc',
            backgroundColor: '#eee'
          }}
        >
          Go
        </button>
      </div>
    </div>
  );
}
